// Sentry
import * as Sentry from '@sentry/react';

// GQL
import { client } from '../client';
import { formatUserFullName } from '../../utils/registeredByOperators';
import debug from 'debug';
import i18n from '../../i18n';

// Queries
import {
  GET_ACCIDENT_BY_ID,
  GET_CONDITIONS_DRIVING_BY_ID,
  GET_IMAGE_BY_ID,
  GET_LUMINOSITY_BY_ID,
  GET_MEDIUM_KNOWLEDGE_BY_ID,
  GET_SURFACE_CONDITION_BY_ID,
  GET_SURVEILLANCE_BODY_BY_ID,
  GET_WEATHER_BY_ID,
} from '../queries/accidents/accidents';
import {
  // GET_VEHICLE_BY_ID, // 🎯 ELIMINADO: No usado después de migración
  // GET_VEHICLE_OCCUPANT_BY_ID, // 🎯 ELIMINADO: Los ocupantes ahora están en VehicleAccidentContext
  GET_VEHICLE_CONTEXTS_BY_ACCIDENT,
} from '../queries/vehicles';
import { GET_CONCESSIONS, GET_ORDER_BY_ID } from '../queries/orders/orders';
import {
  FETCH_CAPITOLS,
  FETCH_PREDEFINED_ORDERS,
  FETCH_SUB_CAPITOLS,
  GET_CAPITOL_BY_ID,
  GET_SUB_CAPITOL_BY_ID,
} from '../queries/orders/predefinedData';
import {
  GET_CONCESSION_BY_ID,
  GET_ROADS,
  GET_ROAD_BY_ID,
} from '../queries/geoPositions/roads';
import { GET_TEAM_BY_ID, FETCH_USERS, GET_USER_BY_ID } from '../queries/users';
import { GET_ATTENTION_ACCIDENT_BY_ID } from '../queries/accidents/attentionAccidents';
import { warmupOrdersByIds } from './warmupOrders';
import { loadLaneCutIds } from './laneCutCatalog';

// Utils
import { pkFormatter } from '../../utils/transform';
import {
  resolveIsLaneCut,
  needsLaneCutFallback,
  EMPTY_LANE_CUT_IDS,
} from '../../utils/laneCut';
import { formatDirectionCellLabel } from '../../utils/surveillanceRoute';
import { ensureLabelNamespaces } from '../../utils/i18nReady';

// Types
// import { ChildVehicleType } from '../../components/display/tables/summaryTables/VehiclesInvolvedSummary/types';

/**
 * Nombre completo de un usuario para pintarlo en una ficha. Cache-first: es
 * dato maestro que `FetchUsers` ya trae, así que normalmente no sale a red.
 * Devuelve '' si no hay id o no resuelve — nunca lanza.
 */
const nombreDeUsuario = async (id?: string | null): Promise<string> => {
  if (!id) return '';
  try {
    const { data } = await client.query({
      query: GET_USER_BY_ID,
      variables: { input: { id } },
      fetchPolicy: 'cache-first',
    });
    return formatUserFullName(data?.user);
  } catch {
    return '';
  }
};

export const selectVehiclesAndDamagesFromAccident = async (orderId: string) => {
  try {
    const {
      data: { order },
    } = await client.query({
      query: GET_ORDER_BY_ID,
      variables: { id: orderId },
    });

    const {
      data: { accident },
    } = await client.query({
      query: GET_ACCIDENT_BY_ID,
      variables: {
        id: order.accidentIDs[0],
      },
    });

    // 🎯 NUEVA IMPLEMENTACIÓN: Usar VehicleAccidentContext
    const {
      data: { vehicleContextsByAccident },
    } = await client.query({
      query: GET_VEHICLE_CONTEXTS_BY_ACCIDENT,
      variables: { accidentId: accident.id },
    });

    const vehiclesParsed: Record<string, unknown> = {};
    let charCode = 'A'.charCodeAt(0);
    const letters: string[] = [];

    // Procesar solo contextos de vehículos principales (no trailers)
    const mainVehicleContexts = vehicleContextsByAccident.filter(
      (context: { isTrailer?: boolean }) => !context.isTrailer
    );

    for (const vehicleContext of mainVehicleContexts) {
      const currentLetter = String.fromCharCode(charCode);

      // 🎯 NUEVO: Usar numberOccupants directamente desde VehicleAccidentContext
      const occupantsVehicle = vehicleContext.numberOccupants || 0;
      console.log(
        `✅ [selectVehiclesAndDamagesFromAccident] Ocupantes desde contexto para ${vehicleContext.plate}: ${occupantsVehicle}`
      );

      // ✅ PATRÓN CONSISTENTE: Detectar trailers usando childVehicleContextIDs
      // Buscar contextos de trailers que tienen este vehículo como parent
      const trailerContexts = vehicleContextsByAccident.filter(
        (ctx: { parentVehicleContextId?: string }) =>
          ctx.parentVehicleContextId === vehicleContext.id
      );
      const hasTrailers = trailerContexts.length > 0;

      // ✅ PATRÓN ORIGINAL: Datos del vehículo principal + trailer si existe
      vehiclesParsed[currentLetter] = hasTrailers
        ? {
            colorVehicle: vehicleContext.color,
            insurerVehicle: vehicleContext.insurance,
            insurerTrailer: trailerContexts[0]?.insurance,
            isTrailerActive: 'true',
            modelVehicle: vehicleContext.model,
            occupantsVehicle: occupantsVehicle,
            policyVehicle: vehicleContext.policyNumber,
            policyTrailer: trailerContexts[0]?.policyNumber,
            registVehicle: vehicleContext.plate,
            registTrailer: trailerContexts[0]?.plate,
          }
        : {
            colorVehicle: vehicleContext.color,
            insurerVehicle: vehicleContext.insurance,
            isTrailerActive: 'false',
            modelVehicle: vehicleContext.model,
            occupantsVehicle: occupantsVehicle,
            policyVehicle: vehicleContext.policyNumber,
            registVehicle: vehicleContext.plate,
          };

      letters.push(currentLetter);
      charCode++;
    }

    return {
      id: accident.id,
      letters: JSON.stringify(letters),
      vehicles: vehiclesParsed,
      damages: accident.damages,
      damagesSwitch: accident.damages ? 'true' : 'false',
      involvedVehiclesSwitch: letters.length > 0 ? 'true' : 'false',
    };
  } catch (err) {
    Sentry.captureException(err);
    console.log('[accidentes] error obteniendo los datos', err);
  }
};

export const selectBasicAccidentInformation = async (orderId: string) => {
  try {
    const {
      data: { order },
    } = await client.query({
      query: GET_ORDER_BY_ID,
      variables: { id: orderId },
    });

    const orderExpeditionParent = await client.query({
      query: GET_ORDER_BY_ID,
      variables: {
        id: order.parentExpeditionOrderIDs[0],
      },
    });

    const {
      data: { accident },
    } = await client.query({
      query: GET_ACCIDENT_BY_ID,
      variables: {
        id: order.accidentIDs[0],
      },
    });

    let dataSurveillance,
      sketch: any = null;
    // C-11.13: candidates para defense-in-depth contra race condition.
    const sketchCandidates: any[] = [];

    if (accident.surveillanceBodyIDs.length > 0) {
      const {
        data: { surveillanceBody },
      } = await client.query({
        query: GET_SURVEILLANCE_BODY_BY_ID,
        variables: {
          id: accident.surveillanceBodyIDs[0],
        },
      });
      dataSurveillance = surveillanceBody;
    }

    const [
      {
        data: { attentionAccident },
      },
      {
        data: { conditionsDriving },
      },
      {
        data: { luminosity },
      },
      {
        data: { mediumKnowledge },
      },
      {
        data: { road },
      },
      {
        data: { surfaceCondition },
      },
      {
        data: { weather },
      },
      images,
    ] = await Promise.all([
      client.query({
        query: GET_ATTENTION_ACCIDENT_BY_ID,
        variables: {
          id: accident.attentionAccidentIDs[0],
        },
      }),
      client.query({
        query: GET_CONDITIONS_DRIVING_BY_ID,
        variables: {
          id: accident.conditionsDrivingIDs[0],
        },
      }),
      client.query({
        query: GET_LUMINOSITY_BY_ID,
        variables: {
          id: accident.luminosityIDs[0],
        },
      }),
      client.query({
        query: GET_MEDIUM_KNOWLEDGE_BY_ID,
        variables: {
          id: accident.mediumKnowledgeIDs[0],
        },
      }),
      client.query({
        query: GET_ROAD_BY_ID,
        variables: {
          id: accident.roadIDs[0],
        },
      }),
      client.query({
        query: GET_SURFACE_CONDITION_BY_ID,
        variables: {
          id: accident.surfaceConditionIDs[0],
        },
      }),
      client.query({
        query: GET_WEATHER_BY_ID,
        variables: {
          id: accident.weatherIDs[0],
        },
      }),
      // C-11.13: recolectar candidatos de sketch en una lista en lugar de
      // asignar `sketch = ...` dentro del Promise.all. Defense-in-depth para
      // race condition: si por cualquier razón (regresión, sync offline, edits
      // legacy) hay múltiples records `_canvas` en `Order.imageIDs[]`, este
      // selector ahora siempre escoge el más reciente por `creatingDate`.
      // Mismo patrón que `selectActionById` y `selectEditionValues`.
      Promise.all(
        order?.imageIDs?.map(async (imageID: string) => {
          let canvas = false;
          const {
            data: { image },
          } = await client.query({
            query: GET_IMAGE_BY_ID,
            variables: {
              id: imageID,
            },
          });
          if (!image) return null;
          // Check if this is a canvas/sketch image
          if (image.name.includes('_canvas')) {
            canvas = true;
            // T56 FIX: Recolectar el blob raw (con `creatingDate` del server)
            // para poder ordenar después por fecha. La transformación al shape
            // `Sketch` (con `date`) se hace una vez tras el sort, igual que
            // `selectEditionValues` en `selectors/actions.ts`.
            sketchCandidates.push({
              id: image.id,
              databaseId: image.id,
              creatingDate: image.creatingDate,
              name: image.name,
            });
          }
          return { ...image, canvas };
        })
      ),
    ]);

    // C-11.13: si hay >1 sketch en `Order.imageIDs[]` (regresión, sync offline,
    // edits previos al fix) elegir el más reciente por `creatingDate`. Si
    // sólo hay 1 (caso normal) el comportamiento es idéntico al anterior.
    // Renombramos `creatingDate` → `date` para cumplir el contrato `Sketch`
    // que consume `AccidentAttention` (el form lee `sketch.date`).
    if (sketchCandidates.length > 0) {
      sketchCandidates.sort(
        (a, b) =>
          new Date(b.creatingDate).getTime() -
          new Date(a.creatingDate).getTime(),
      );
      const latest = sketchCandidates[0];
      sketch = {
        id: latest.id, // se sobrescribe por handleAddSketch con data URI al repintar
        databaseId: latest.databaseId, // T56: preservado entre repintados
        name: latest.name,
        date: latest.creatingDate,
        road: '', // se asigna desde defaultValues?.road en AccidentAttention.tsx
      };
    }

    return {
      id: accident.id,
      laneCutting: accident.laneCutting,
      policeReport: accident.policeReport,
      policeReportNumber: accident?.policeReportNumber,
      roadConditionDescription: accident.roadConditionDescription,
      conditionsDriving: conditionsDriving.name,
      luminosity: luminosity.name,
      mediumKnowledge: mediumKnowledge.name,
      road: road.name,
      surfaceCondition: surfaceCondition.name,
      surveillanceBody: dataSurveillance?.name,
      weather: weather.name,
      orderId: order.id,
      roid: order.roid,
      noticeConservationCenterDate:
        attentionAccident?.noticeConservationCenterDate,
      arrivalConservationCenterDate:
        attentionAccident?.arrivalConservationCenterDate,
      departureConservationCenterDate:
        attentionAccident?.departureConservationCenterDate,
      noticeControlCenterDate: attentionAccident?.noticeControlCenterDate,
      arrivalControlCenterDate: attentionAccident?.arrivalControlCenterDate,
      departureControlCenterDate: attentionAccident?.departureControlCenterDate,
      noticeSquadBoysDate: attentionAccident?.noticeSquadBoysDate,
      arrivalSquadBoysDate: attentionAccident?.arrivalSquadBoysDate,
      departureSquadBoysDate: attentionAccident?.departureSquadBoysDate,
      noticeAmbulanceCraneAssistanceDate:
        attentionAccident?.noticeAmbulanceCraneAssistanceDate,
      arrivalAmbulanceCraneAssistanceDate:
        attentionAccident?.arrivalAmbulanceCraneAssistanceDate,
      departureAmbulanceCraneAssistanceDate:
        attentionAccident?.departureAmbulanceCraneAssistanceDate,
      noticeRoadTeamDate: attentionAccident?.noticeRoadTeamDate,
      arrivalRoadTeamDate: attentionAccident?.arrivalRoadTeamDate,
      departureRoadTeamDate: attentionAccident?.departureRoadTeamDate,
      noticeOtherDate: attentionAccident?.noticeOtherDate,
      arrivalOtherDate: attentionAccident?.arrivalOtherDate,
      departureOtherDate: attentionAccident?.departureOtherDate,
      otherName: attentionAccident?.otherName,
      images: JSON.stringify(images.filter((image) => !image?.canvas)),
      // TODO: el sketch se esta cargando tambien por otra parte no se donde y sigue haciendo bucle infinito,
      // estaba tratando de tener el sketch por los demas lados para que luego llegara el dato y se usa en el front
      sketch: sketch ? JSON.stringify(sketch) : null,
      stateOrderExpeditionParent: orderExpeditionParent.data.order.state,
    };
  } catch (err) {
    Sentry.captureException(err);
    console.log('[accidentes] error obteniendo la información básica', err);
  }
};

export const selectOrderOfAccidentById = async (orderId: string) => {
  const logSelector = debug('rois:selector:accident:edit');
  debug.enable('rois:selector:accident:edit');

  try {
    const {
      data: { order },
    } = await client.query({
      query: GET_ORDER_BY_ID,
      variables: { id: orderId },
    });

    // 🔍 DEBUG: Verificar predefinedOrderIDs en la Order
    logSelector(`🔍 [selectOrderOfAccidentById] orderId: ${orderId}`);
    logSelector(`🔍 [selectOrderOfAccidentById] order.id: ${order.id}`);
    logSelector(`📋 [selectOrderOfAccidentById] order.predefinedOrderIDs: ${JSON.stringify(order.predefinedOrderIDs)}`);

    const orderExpeditionParent = await client.query({
      query: GET_ORDER_BY_ID,
      variables: {
        id: order.parentExpeditionOrderIDs[0],
      },
    });

    const {
      data: { accident },
    } = await client.query({
      query: GET_ACCIDENT_BY_ID,
      variables: {
        id: order.accidentIDs[0],
      },
    });

    const [
      {
        data: { concession },
      },
      {
        data: { road },
      },
      {
        data: { capitol },
      },
      {
        data: { subCapitol },
      },
    ] = await Promise.all([
      client.query({
        query: GET_CONCESSION_BY_ID,
        variables: {
          id: accident.concessionIDs[0],
        },
      }),
      client.query({
        query: GET_ROAD_BY_ID,
        variables: {
          id: accident.roadIDs[0],
        },
      }),
      client.query({
        query: GET_CAPITOL_BY_ID,
        variables: {
          id: order.capitolIDs[0],
        },
      }),
      client.query({
        query: GET_SUB_CAPITOL_BY_ID,
        variables: {
          id: order.subCapitolIDs[0],
        },
      }),
    ]);

    // ✅ M2M: Fetch ALL teams, not just first
    let teamValues: any[] = [];
    if (accident?.teamIDs?.length > 0) {
      const teamQueries = accident.teamIDs.map((teamId: string) =>
        client.query({
          query: GET_TEAM_BY_ID,
          variables: { id: teamId },
        })
      );
      const teamResults = await Promise.all(teamQueries);
      teamValues = teamResults.map((result) => result.data.team);
    }

    return {
      accidentId: accident.id,
      attentionAccidentId: accident.attentionAccidentIDs[0],
      direction: accident.direction,
      lane: accident.lane,
      margin: accident.margin,
      pkInitRoad: pkFormatter(road.pkInitKm, road.pkInitMeter),
      pkEndRoad: pkFormatter(road.pkEndKm, road.pkEndMeter),
      pkInit: pkFormatter(accident.pkInitKm, accident.pkInitMeter),
      pkEnd: pkFormatter(accident.pkEndKm, accident.pkEndMeter),
      track: accident.track,
      concession: accident.concessionIDs[0], // ✅ Envía el ID, no el nombre
      road: road.id, // ✅ FIX: Enviar ID en lugar de nombre para que el dropdown funcione
      teams: teamValues.map((t) => t.name), // ✅ M2M: Return array of team names
      orderId: order.id,
      roid: order.roid,
      description: order.description,
      notes: order.notes,
      type: order.type,
      registerInitDate: order.registerInitDate,
      capitol: capitol.id,
      subCapitol: subCapitol.id,
      predefine: order.predefinedOrderIDs?.[0], // ✅ FIX: Añadir predefinido para auto-selección
      stateOrderExpeditionParent: orderExpeditionParent.data.order.state,
      // El padre según el servidor: a dónde vuelve el guardado de la edición
      // cuando el contexto de navegación no es de este accidente (spec
      // wizard-draft-ownership § 6; mismo criterio que `parentOrderIdFromData`
      // del resumen de actuación). Viaja en la copia del paso 1 y en su
      // `dataToCompare`, así que no entra en el diff del guardado.
      parentOrderIdFromData: orderExpeditionParent.data.order.id,
      parentOrderTypeFromData: orderExpeditionParent.data.order.type,
      userId: order.userId,
      // Operario que registra (editable en edición, no obligatorio). `?? ''`
      // representa "sin operario" en históricos: así el dropdown queda vacío en
      // vez de que NewWorkOrderForm lo pre-rellene con el usuario logueado (su
      // fallback `?? loggedUser` solo aplica a null/undefined), evitando estampar
      // al editor en un histórico por un diff espurio. Entra también en
      // `dataToCompare` (dataToCompare = este objeto) → el diff solo dispara
      // cuando el usuario cambia el operario de verdad.
      registeredByUserId: order.registeredByUserId ?? '',
      city: concession.location,
    };
  } catch (err) {
    Sentry.captureException(err);
    console.log('[accidentes] error obteniendo la orden de trabajo', err);
  }
};

export const selectAccidentByOrderId = async (orderId: string) => {
  const logSelector = debug('rois:selector:accident');
  debug.enable('rois:selector:accident');
  logSelector(`🔍 Iniciando selectAccidentByOrderId para orderId: ${orderId}`);

  try {
    const {
      data: { order },
    } = await client.query({
      query: GET_ORDER_BY_ID,
      variables: { id: orderId },
    });

    logSelector(
      `✅ Order obtenida: ${order.id}, accidentIDs: ${
        order.accidentIDs?.length || 0
      }`
    );

    const orderExpeditionParent = await client.query({
      query: GET_ORDER_BY_ID,
      variables: {
        id: order.parentExpeditionOrderIDs[0],
      },
    });

    const {
      data: { accident },
    } = await client.query({
      query: GET_ACCIDENT_BY_ID,
      variables: {
        id: order.accidentIDs[0],
      },
    });

    const [
      {
        data: { capitols },
      },
      {
        data: { concessions },
      },
      {
        data: { roads },
      },
      {
        data: { subCapitols },
      },
    ] = await Promise.all([
      client.query({
        query: FETCH_CAPITOLS,
      }),
      client.query({
        query: GET_CONCESSIONS,
      }),
      client.query({
        query: GET_ROADS,
      }),
      client.query({
        query: FETCH_SUB_CAPITOLS,
      }),
    ]);

    let dataSurveillance;

    if (accident.surveillanceBodyIDs.length > 0) {
      const {
        data: { surveillanceBody },
      } = await client.query({
        query: GET_SURVEILLANCE_BODY_BY_ID,
        variables: {
          id: accident.surveillanceBodyIDs[0],
        },
      });
      dataSurveillance = surveillanceBody;
    }

    const [
      {
        data: { attentionAccident },
      },
      {
        data: { conditionsDriving },
      },
      {
        data: { concession },
      },
      {
        data: { luminosity },
      },
      {
        data: { mediumKnowledge },
      },
      {
        data: { road },
      },
      {
        data: { surfaceCondition },
      },
      {
        data: { weather },
      },
      {
        data: { capitol },
      },
      {
        data: { subCapitol },
      },
      vehicles,
      images,
    ] = await Promise.all([
      client.query({
        query: GET_ATTENTION_ACCIDENT_BY_ID,
        variables: {
          id: accident.attentionAccidentIDs[0],
        },
      }),
      client.query({
        query: GET_CONDITIONS_DRIVING_BY_ID,
        variables: {
          id: accident.conditionsDrivingIDs[0],
        },
      }),
      client.query({
        query: GET_CONCESSION_BY_ID,
        variables: {
          id: accident.concessionIDs[0],
        },
      }),
      client.query({
        query: GET_LUMINOSITY_BY_ID,
        variables: {
          id: accident.luminosityIDs[0],
        },
      }),
      client.query({
        query: GET_MEDIUM_KNOWLEDGE_BY_ID,
        variables: {
          id: accident.mediumKnowledgeIDs[0],
        },
      }),
      client.query({
        query: GET_ROAD_BY_ID,
        variables: {
          id: accident.roadIDs[0],
        },
      }),
      client.query({
        query: GET_SURFACE_CONDITION_BY_ID,
        variables: {
          id: accident.surfaceConditionIDs[0],
        },
      }),
      client.query({
        query: GET_WEATHER_BY_ID,
        variables: {
          id: accident.weatherIDs[0],
        },
      }),
      client.query({
        query: GET_CAPITOL_BY_ID,
        variables: {
          id: order.capitolIDs[0],
        },
      }),
      client.query({
        query: GET_SUB_CAPITOL_BY_ID,
        variables: {
          id: order.subCapitolIDs[0],
        },
      }),
      // 🎯 NUEVA IMPLEMENTACIÓN: Usar VehicleAccidentContext para obtener vehículos
      client
        .query({
          query: GET_VEHICLE_CONTEXTS_BY_ACCIDENT,
          variables: { accidentId: accident.id },
        })
        .then(async ({ data: { vehicleContextsByAccident } }) => {
          logSelector(
            `🚗 Contextos de vehículos obtenidos: ${
              vehicleContextsByAccident?.length || 0
            } contextos para accidente ${accident.id}`
          );

          // Procesar contextos para devolver datos en formato compatible
          const vehiclesWithContext = [];

          for (const context of vehicleContextsByAccident) {
            logSelector(
              `📋 Procesando contexto: ${context.plate}, isTrailer: ${context.isTrailer}, vehicleId: ${context.vehicleId}`
            );

            // Solo procesar vehículos principales (no trailers)
            if (!context.isTrailer) {
              // 🎯 NUEVO: Usar numberOccupants directamente desde VehicleAccidentContext
              const numberOccupants = context.numberOccupants || 0;

              logSelector(
                `✅ Ocupantes desde contexto para ${context.plate}: ${numberOccupants}`
              );

              // 🎯 CRÍTICO: Cargar trailers reales para el resumen
              // Buscar contextos de trailers que tienen este vehículo como parent
              const trailerContexts = vehicleContextsByAccident.filter(
                (ctx: { parentVehicleContextId?: string }) =>
                  ctx.parentVehicleContextId === context.id
              );

              // Crear objetos ChildVehicle para cada trailer (solo datos puros)
              const childVehicles = trailerContexts.map(
                (trailerCtx: {
                  vehicleId: string;
                  plate?: string;
                  model?: string;
                  color?: string;
                  insurance?: string;
                  policyNumber?: string;
                }) => ({
                  id: trailerCtx.vehicleId,
                  plate: trailerCtx.plate,
                  model: trailerCtx.model || null, // Los trailers pueden no tener modelo
                  color: trailerCtx.color,
                  insurance: trailerCtx.insurance,
                  policyNumber: trailerCtx.policyNumber,
                  isTrailer: true,
                  // ✅ CORREGIDO: No incluir campos de UI en el selector
                  // El campo 'vehicle' se asignará en el componente VehiclesInvolvedSummary
                })
              );

              logSelector(
                `🚚 Trailers encontrados para ${context.plate}: ${childVehicles.length} trailers`
              );

              // Crear objeto vehículo usando datos del contexto (históricos)
              const vehicleWithContext = {
                id: context.vehicleId,
                plate: context.plate,
                model: context.model,
                color: context.color,
                category: context.category,
                insurance: context.insurance,
                policyNumber: context.policyNumber,
                isTrailer: context.isTrailer,
                isInternal: context.isInternal,
                isActive: context.isActive,
                numberOccupants: numberOccupants,
                // ✅ CORREGIDO: Cargar trailers reales para mostrar en el resumen
                childVehicle: childVehicles,
                // Mantener campos requeridos por compatibilidad
                accidentIDs: [accident.id],
                // 🎯 ELIMINADO: vehicleOccupantIDs - Los ocupantes ahora están en VehicleAccidentContext
                parentVehicleIDs: context.parentVehicleContextId
                  ? [context.parentVehicleContextId]
                  : [],
                childVehicleIDs: context.childVehicleContextIDs || [],
                vehicleCategoryIDs: context.vehicle?.vehicleCategoryIDs || [],
                concessionIDs: context.vehicle?.concessionIDs || [],
              };

              vehiclesWithContext.push(vehicleWithContext);
              logSelector(
                `✅ Vehículo procesado: ${context.plate} con ${childVehicles.length} trailers`
              );
            }
          }

          logSelector(
            `🎯 Total vehículos principales procesados: ${vehiclesWithContext.length}`
          );
          return vehiclesWithContext;
        }),
      Promise.all(
        order?.imageIDs?.map(async (imageID: string) => {
          const {
            data: { image },
          } = await client.query({
            query: GET_IMAGE_BY_ID,
            variables: {
              id: imageID,
            },
          });
          return image;
        })
      ),
    ]);

    // ✅ M2M: Fetch ALL teams, not just first
    let teamValues: any[] = [];
    if (accident?.teamIDs?.length > 0) {
      const teamQueries = accident.teamIDs.map((teamId: string) =>
        client.query({
          query: GET_TEAM_BY_ID,
          variables: { id: teamId },
        })
      );
      const teamResults = await Promise.all(teamQueries);
      teamValues = teamResults.map((result) => result.data.team);
    }

    return {
      id: accident.id,
      direction: accident.direction,
      lane: accident.lane,
      laneCutting: accident.laneCutting,
      margin: accident.margin,
      pkInitRoad: pkFormatter(accident.pkInitKm, accident.pkInitMeter),
      pkEndRoad: pkFormatter(accident.pkEndKm, accident.pkEndMeter),
      pkInit: pkFormatter(accident.pkInitKm, accident.pkInitMeter),
      pkEnd: pkFormatter(accident.pkEndKm, accident.pkEndMeter),
      policeReport: accident.policeReport,
      policeReportNumber: accident?.policeReportNumber,
      roadConditionDescription: accident.roadConditionDescription,
      track: accident.track,
      // ✅ FIX: ID (no name). El <WorkOrder> del resumen resuelve el nombre él
      // mismo vía GetConcessionById/GetRoadById; pasar el nombre ("TR"/"C-25")
      // reventaba con "Malformed ObjectID" al cargar el resumen del accidente.
      // Espejo del fix ya presente en la función hermana selectAccidentById.
      concession: accident.concessionIDs[0],
      conditionsDriving: conditionsDriving.name,
      luminosity: luminosity.name,
      mediumKnowledge: mediumKnowledge.name,
      road: road.id,
      surfaceCondition: surfaceCondition.name,
      surveillanceBody: dataSurveillance?.name,
      teams: teamValues.map((t) => t.name), // ✅ M2M: Return array of team names
      vehicles: vehicles,
      weather: weather.name,
      orderId: order.id,
      roid: order.roid,
      description: order.description,
      notes: order.notes,
      state: order.state,
      type: order.type,
      tag: order.specialTag,
      registerInitDate: order.registerInitDate,
      capitol: capitol.id,
      subCapitol: subCapitol.id,
      capitols: capitols,
      concessions: concessions,
      roadsValues: roads,
      subCapitolsValues: subCapitols,
      noticeConservationCenterDate:
        attentionAccident?.noticeConservationCenterDate,
      arrivalConservationCenterDate:
        attentionAccident?.arrivalConservationCenterDate,
      departureConservationCenterDate:
        attentionAccident?.departureConservationCenterDate,
      noticeControlCenterDate: attentionAccident?.noticeControlCenterDate,
      arrivalControlCenterDate: attentionAccident?.arrivalControlCenterDate,
      departureControlCenterDate: attentionAccident?.departureControlCenterDate,
      noticeSquadBoysDate: attentionAccident?.noticeSquadBoysDate,
      arrivalSquadBoysDate: attentionAccident?.arrivalSquadBoysDate,
      departureSquadBoysDate: attentionAccident?.departureSquadBoysDate,
      noticeAmbulanceCraneAssistanceDate:
        attentionAccident?.noticeAmbulanceCraneAssistanceDate,
      arrivalAmbulanceCraneAssistanceDate:
        attentionAccident?.arrivalAmbulanceCraneAssistanceDate,
      departureAmbulanceCraneAssistanceDate:
        attentionAccident?.departureAmbulanceCraneAssistanceDate,
      noticeRoadTeamDate: attentionAccident?.noticeRoadTeamDate,
      arrivalRoadTeamDate: attentionAccident?.arrivalRoadTeamDate,
      departureRoadTeamDate: attentionAccident?.departureRoadTeamDate,
      noticeOtherDate: attentionAccident?.noticeOtherDate,
      arrivalOtherDate: attentionAccident?.arrivalOtherDate,
      departureOtherDate: attentionAccident?.departureOtherDate,
      otherName: attentionAccident?.otherName,
      images: images,
      damages: accident.damages,
      stateOrderExpeditionParent: orderExpeditionParent.data.order.state,
      userId: order.userId,
      // Quién creó el registro y quién lo rellenó. La ficha (ShowOT) ya los
      // enseñaba y este resumen no. Tolerante a fallos: un nombre que no
      // resuelve deja la fila fuera, nunca tumba la pantalla.
      creatorName: await nombreDeUsuario(order.userId),
      registeredByName: await nombreDeUsuario(order.registeredByUserId),
      city: concession.location,
      predefine: order.predefinedOrderIDs?.[0], // ✅ FIX: Añadir predefinido para mostrar en resumen
    };

  } catch (err) {
    logSelector(
      `❌ Error en selectAccidentByOrderId para orderId: ${orderId}`,
      err
    );
    Sentry.captureException(err);
    console.log('[accidentes] error obteniendo los datos', err);
  }
};

export const selectAccidentOrders = async (
  accidents: any[] = [],
  reFetch = false
) => {
  // El Sentido se formatea aquí y queda congelado en la fila: sin las
  // traducciones cargadas la celda enseñaría la clave en crudo para siempre.
  // Ver `utils/i18nReady.ts`.
  await ensureLabelNamespaces();

  // Resolver creador (Order.userId → "Nombre Apellido") leyendo FETCH_USERS
  // de la caché Apollo. El loader pre-carga FETCH_USERS antes de invocar
  // este selector, así que readQuery da hit. Patrón espejo del usado en
  // selectActionOrders (src/gql/selectors/actions.ts:1259-1270).
  const creatorMap: Record<string, string> = {};
  try {
    const usersData = client.readQuery({ query: FETCH_USERS });
    if (usersData?.users) {
      usersData.users.forEach(
        (u: { id: string; name?: string; surName?: string }) => {
          creatorMap[u.id] = `${u.name ?? ''} ${u.surName ?? ''}`.trim();
        },
      );
    }
  } catch {
    // Cache no disponible — columna Creador queda vacía como fallback
  }

  // Warm-up: UNA query `ordersByIds` puebla la caché con las Orders paralelas
  // de todas las filas; los GET_ORDER_BY_ID per-row de abajo pasan a
  // resolverse de caché (redirect Query.order). Si falla → no-op, per-row
  // intacto como fallback natural.
  await warmupOrdersByIds(
    accidents.map((a) => a?.orderIDs?.[0]),
    reFetch
  );

  // Ids de catálogo que son corte de carril: SOLO si alguna fila carece del
  // campo `isLaneCut` del servidor. Con datos ya sincronizados esto ahorra las
  // dos queries del catálogo (FETCH_ACTIVITYS + FETCH_PREDEFINED_ORDERS) en
  // cada listado; el respaldo local solo hace falta para lo no sincronizado.
  const laneCutIds = needsLaneCutFallback(accidents)
    ? await loadLaneCutIds()
    : EMPTY_LANE_CUT_IDS;

  // Resiliencia POR FILA (paridad con `selectActionOrders` y `selectAllOrders`,
  // que ya la tienen): el guard de Order huérfana de más abajo cubre un solo
  // modo de fallo. Los catálogos se piden con `xxxIDs[0]` — si el array viene
  // vacío el id es `undefined`, y si el maestro fue borrado la query resuelve
  // `null`; en ambos casos el `.name` lanzaba TypeError. Con `Promise.all` esa
  // promesa rechazada tumbaba EL LOTE ENTERO: tabla de accidentes en blanco y
  // el Excel de históricos cayendo al fallback DOM, que solo exporta la página
  // visible ("alguna fila no sale"). `allSettled` + los `?.` del objeto de
  // retorno descartan únicamente la fila irrecuperable. Happy path idéntico.
  const settled = await Promise.allSettled(
    accidents.map(async (accident) => {
      const {
        data: { order },
      } = await client.query({
        query: GET_ORDER_BY_ID,
        variables: {
          id: accident.orderIDs[0],
        },
        fetchPolicy: reFetch ? 'network-only' : 'cache-first',
      });

      // Resiliencia ante datos huérfanos: la Order padre puede resolver a null
      // (Accident huérfano cuyo Order fue borrado → GET_ORDER_BY_ID = null). Sin
      // este guard, `order.userId` reventaba más abajo (TypeError) y, al no
      // haber catch por-fila, el Promise.all rechazaba TODA la tabla por una
      // sola fila corrupta. Saltamos la fila (se filtra abajo) y dejamos traza
      // para depurar la integridad de datos.
      if (!order) {
        console.warn(
          `[selectAccidentOrders] Order padre no resuelta (huérfana), fila omitida — accidentId=${accident.id} orderId=${accident.orderIDs?.[0]}`,
        );
        return null;
      }

      const [
        {
          data: { concession },
        },
        {
          data: { mediumKnowledge },
        },
        {
          data: { weather },
        },
        {
          data: { road },
        },
      ] = await Promise.all([
        client.query({
          query: GET_CONCESSION_BY_ID,
          variables: {
            id: accident.concessionIDs[0],
          },
          fetchPolicy: reFetch ? 'network-only' : 'cache-first',
        }),
        client.query({
          query: GET_MEDIUM_KNOWLEDGE_BY_ID,
          variables: {
            id: accident.mediumKnowledgeIDs[0],
          },
          fetchPolicy: reFetch ? 'network-only' : 'cache-first',
        }),
        client.query({
          query: GET_WEATHER_BY_ID,
          variables: {
            id: accident.weatherIDs[0],
          },
          fetchPolicy: reFetch ? 'network-only' : 'cache-first',
        }),
        client.query({
          query: GET_ROAD_BY_ID,
          variables: {
            id: accident.roadIDs[0],
          },
          fetchPolicy: reFetch ? 'network-only' : 'cache-first',
        }),
      ]);

      let predefinedOrderName = '';
      const predefinedId = order.predefinedOrderIDs?.[0];
      if (predefinedId) {
        try {
          const cached = client.readQuery({ query: FETCH_PREDEFINED_ORDERS });
          predefinedOrderName = cached?.predefinedOrders?.find((p: any) => p.id === predefinedId)?.name ?? '';
        } catch { /* cache not available */ }
      }

      return {
        id: order?.id,
        roid:
          typeof order?.roid === 'string'
            ? order.roid.split('.')
            : Array.isArray(order?.roid)
              ? order.roid
              : [],
        registerInitDate: order.registerInitDate,
        creationDate: order.creationDate,
        concession: concession?.name ?? '',
        road: road?.name ?? '',
        // Display unificado de "Sentido" (helper compartido con
        // `selectAllOrders`) — formato "Texto (símbolo)". Alimenta la columna
        // Sentido del Excel de históricos de accidentes.
        direction: formatDirectionCellLabel(accident.direction, road?.directions),
        description: order?.description,
        pkInit: pkFormatter(accident.pkInitKm, accident.pkInitMeter),
        pkEnd: pkFormatter(accident.pkEndKm, accident.pkEndMeter),
        weather: weather?.name ?? '',
        mediumKnowledge: mediumKnowledge?.name ?? '',
        accidentID: accident.id,
        state: order.state,
        predefinedOrder: predefinedOrderName,
        // Corte de carril unificado. `Accident` NO tiene relación con Activity,
        // así que solo aplican el flag y la OT predefinida.
        // Manda el valor del backend (`Accident.isLaneCut`). El cálculo local
        // solo cubre la fila sin sincronizar; lee la predefinida de la ORDER,
        // que es donde la tiene el front (el backend une entidad + gemela).
        isLaneCut: resolveIsLaneCut(
          accident.isLaneCut,
          {
            laneCutting: accident.laneCutting,
            predefinedOrderIDs: order.predefinedOrderIDs,
          },
          laneCutIds,
        ),
        user: order.userId ? (creatorMap[order.userId] ?? '') : '',
        registeredBy: order.registeredByUserId ? (creatorMap[order.registeredByUserId] ?? '') : '',
        // Fase 4 nav Trabajo/Revisión (follow-up T4, 2026-07-12) — "Origen"
        // viene directo del campo GraphQL `Accident.containerType` (el
        // backend lo denormaliza en Prisma; ver NEW_ACCIDENT fragment). Ya
        // no se deriva por fila con un lookup extra client-side.
        containerType: accident.containerType,
      };
    })
  );

  return settled
    .map((result, index) => {
      if (result.status === 'fulfilled') return result.value;
      console.warn(
        `[selectAccidentOrders] Fila omitida por error irrecuperable — accidentId=${accidents[index]?.id} orderId=${accidents[index]?.orderIDs?.[0]}`,
        result.reason,
      );
      return null;
    })
    .filter((row): row is NonNullable<typeof row> => row != null);
};

/**
 * selectAccidentById - Obtiene un accidente por su ID directo
 *
 * Similar a selectAccidentByOrderId pero recibe el ID del accidente directamente
 * en lugar del ID de la orden. Útil para navegación desde ShowOT.
 *
 * @param accidentId - ID del accidente
 * @returns Datos completos del accidente con información de la orden padre
 */
export const selectAccidentById = async (accidentId: string) => {
  const logSelector = debug('rois:selector:accident');
  debug.enable('rois:selector:accident');
  logSelector(`🔍 Iniciando selectAccidentById para accidentId: ${accidentId}`);

  try {
    // 1. Obtener el accidente directamente
    const {
      data: { accident },
    } = await client.query({
      query: GET_ACCIDENT_BY_ID,
      variables: { id: accidentId },
      // Explícito (= default del cliente, sin cambio de semántica): en el
      // prefetch de hijas del detalle de OT/campaña la frescura la garantiza
      // el warm-up `accidentsByIds` network-only de
      // `selectWorkOrderWithChildren` (mismo fragment NewAccident, hit vía
      // redirect Query.accident); navegando desde tablas, el hit viene de
      // FETCH_ACCIDENTS (mismo fragment). Si nadie calentó, miss → red = hoy.
      fetchPolicy: 'cache-first',
    });

    logSelector(
      `✅ Accidente obtenido: ${accident.id}, orderIDs: ${
        accident.orderIDs?.length || 0
      }`
    );

    // 2. Obtener la orden asociada al accidente
    const {
      data: { order },
    } = await client.query({
      query: GET_ORDER_BY_ID,
      variables: { id: accident.orderIDs[0] },
    });

    logSelector(
      `✅ Order obtenida: ${order.id}, state: ${order.state}`
    );

    // 3. Obtener la orden padre de expedición
    const orderExpeditionParent = await client.query({
      query: GET_ORDER_BY_ID,
      variables: {
        id: order.parentExpeditionOrderIDs[0],
      },
    });

    // 4. Obtener catálogos base
    const [
      {
        data: { capitols },
      },
      {
        data: { concessions },
      },
      {
        data: { roads },
      },
      {
        data: { subCapitols },
      },
    ] = await Promise.all([
      client.query({
        query: FETCH_CAPITOLS,
      }),
      client.query({
        query: GET_CONCESSIONS,
      }),
      client.query({
        query: GET_ROADS,
      }),
      client.query({
        query: FETCH_SUB_CAPITOLS,
      }),
    ]);

    // 5. Obtener cuerpo de vigilancia si existe
    let dataSurveillance;
    if (accident.surveillanceBodyIDs.length > 0) {
      const {
        data: { surveillanceBody },
      } = await client.query({
        query: GET_SURVEILLANCE_BODY_BY_ID,
        variables: {
          id: accident.surveillanceBodyIDs[0],
        },
      });
      dataSurveillance = surveillanceBody;
    }

    // 6. Obtener datos detallados del accidente
    const [
      {
        data: { attentionAccident },
      },
      {
        data: { conditionsDriving },
      },
      {
        data: { concession },
      },
      {
        data: { luminosity },
      },
      {
        data: { mediumKnowledge },
      },
      {
        data: { road },
      },
      {
        data: { surfaceCondition },
      },
      {
        data: { weather },
      },
      {
        data: { capitol },
      },
      {
        data: { subCapitol },
      },
      vehicles,
      images,
    ] = await Promise.all([
      client.query({
        query: GET_ATTENTION_ACCIDENT_BY_ID,
        variables: {
          id: accident.attentionAccidentIDs[0],
        },
      }),
      client.query({
        query: GET_CONDITIONS_DRIVING_BY_ID,
        variables: {
          id: accident.conditionsDrivingIDs[0],
        },
      }),
      client.query({
        query: GET_CONCESSION_BY_ID,
        variables: {
          id: accident.concessionIDs[0],
        },
      }),
      client.query({
        query: GET_LUMINOSITY_BY_ID,
        variables: {
          id: accident.luminosityIDs[0],
        },
      }),
      client.query({
        query: GET_MEDIUM_KNOWLEDGE_BY_ID,
        variables: {
          id: accident.mediumKnowledgeIDs[0],
        },
      }),
      client.query({
        query: GET_ROAD_BY_ID,
        variables: {
          id: accident.roadIDs[0],
        },
      }),
      client.query({
        query: GET_SURFACE_CONDITION_BY_ID,
        variables: {
          id: accident.surfaceConditionIDs[0],
        },
      }),
      client.query({
        query: GET_WEATHER_BY_ID,
        variables: {
          id: accident.weatherIDs[0],
        },
      }),
      client.query({
        query: GET_CAPITOL_BY_ID,
        variables: {
          id: order.capitolIDs[0],
        },
      }),
      client.query({
        query: GET_SUB_CAPITOL_BY_ID,
        variables: {
          id: order.subCapitolIDs[0],
        },
      }),
      // Obtener vehículos usando VehicleAccidentContext
      client
        .query({
          query: GET_VEHICLE_CONTEXTS_BY_ACCIDENT,
          variables: { accidentId: accident.id },
        })
        .then(async ({ data: { vehicleContextsByAccident } }) => {
          logSelector(
            `🚗 Contextos de vehículos obtenidos: ${
              vehicleContextsByAccident?.length || 0
            } contextos para accidente ${accident.id}`
          );

          const vehiclesWithContext = [];

          for (const context of vehicleContextsByAccident) {
            if (!context.isTrailer) {
              const numberOccupants = context.numberOccupants || 0;

              const trailerContexts = vehicleContextsByAccident.filter(
                (ctx: { parentVehicleContextId?: string }) =>
                  ctx.parentVehicleContextId === context.id
              );

              const childVehicles = trailerContexts.map(
                (trailerCtx: {
                  vehicleId: string;
                  plate?: string;
                  model?: string;
                  color?: string;
                  insurance?: string;
                  policyNumber?: string;
                }) => ({
                  id: trailerCtx.vehicleId,
                  plate: trailerCtx.plate,
                  model: trailerCtx.model || null,
                  color: trailerCtx.color,
                  insurance: trailerCtx.insurance,
                  policyNumber: trailerCtx.policyNumber,
                  isTrailer: true,
                })
              );

              const vehicleWithContext = {
                id: context.vehicleId,
                plate: context.plate,
                model: context.model,
                color: context.color,
                category: context.category,
                insurance: context.insurance,
                policyNumber: context.policyNumber,
                isTrailer: context.isTrailer,
                isInternal: context.isInternal,
                isActive: context.isActive,
                numberOccupants: numberOccupants,
                childVehicle: childVehicles,
                accidentIDs: [accident.id],
                parentVehicleIDs: context.parentVehicleContextId
                  ? [context.parentVehicleContextId]
                  : [],
                childVehicleIDs: context.childVehicleContextIDs || [],
                vehicleCategoryIDs: context.vehicle?.vehicleCategoryIDs || [],
                concessionIDs: context.vehicle?.concessionIDs || [],
              };

              vehiclesWithContext.push(vehicleWithContext);
            }
          }

          return vehiclesWithContext;
        }),
      Promise.all(
        order?.imageIDs?.map(async (imageID: string) => {
          const {
            data: { image },
          } = await client.query({
            query: GET_IMAGE_BY_ID,
            variables: {
              id: imageID,
            },
          });
          return image;
        })
      ),
    ]);

    // 7. Obtener TODOS los equipos (M2M)
    let teamValues: any[] = [];
    if (accident?.teamIDs?.length > 0) {
      const teamQueries = accident.teamIDs.map((teamId: string) =>
        client.query({
          query: GET_TEAM_BY_ID,
          variables: { id: teamId },
        })
      );
      const teamResults = await Promise.all(teamQueries);
      teamValues = teamResults.map((result) => result.data.team);
    }

    logSelector(
      `🎉 selectAccidentById completado exitosamente para accidentId: ${accidentId}`
    );

    // 8. Retornar datos formateados (mismo formato que selectAccidentByOrderId)
    return {
      orderId: order?.id,
      roid: order?.roid,
      // `type` es imprescindible aguas abajo: AccidentSummary lo pasa a WorkOrder
      // y `canRoleChangeEntityState(role, type)` decide si el tag de estado es
      // clicable. Sin él (undefined) el tag quedaba inerte → "no se podía cambiar
      // el estado del accidente desde dentro de la OT". selectAccidentByOrderId ya
      // lo devolvía (L805); aquí faltaba (divergencia entre selectores hermanos).
      type: order?.type,
      registerInitDate: order.registerInitDate,
      creationDate: order.creationDate,
      // Quién creó la orden y quién rellenó los datos. Espejo EXACTO de
      // selectAccidentByOrderId: los dos selectores alimentan el MISMO
      // <AccidentContentCardResume>, y el resumen se abre por las dos rutas
      // (por orderId desde la tabla, por accidentId desde la ficha de la OT).
      // Omitirlo aquí dejaba las dos filas en blanco justo por la ruta que más
      // se usa al validar. Misma divergencia entre hermanos que el `type` de
      // arriba — si tocas uno, toca el otro.
      creatorName: await nombreDeUsuario(order.userId),
      registeredByName: await nombreDeUsuario(order.registeredByUserId),
      // ✅ FIX: ID (no name), espejo del fix de selectAccidentByOrderId. Esta
      // función alimenta el MISMO <WorkOrder> del resumen (por la ruta accidentId),
      // cuyo dropdown resuelve id→label (el usuario ve el nombre) y cuya query
      // filterRoadsByConcession revienta con "Malformed ObjectID" si recibe el
      // nombre. Los campos concessionId/roadId de abajo quedan (nadie los rompe).
      concession: accident.concessionIDs[0],
      road: road.id,
      description: order?.description,
      pkInit: pkFormatter(accident.pkInitKm, accident.pkInitMeter),
      pkEnd: pkFormatter(accident.pkEndKm, accident.pkEndMeter),
      track: accident.track,
      direction: accident.direction,
      margin: accident.margin,
      lane: accident.lane,
      weather: weather.name,
      surfaceCondition: surfaceCondition.name,
      luminosity: luminosity.name,
      conditionsDriving: conditionsDriving.name,
      mediumKnowledge: mediumKnowledge.name,
      surveillanceBody: dataSurveillance?.name,
      teams: teamValues.map((t) => t.name), // ✅ M2M: Return array of team names
      laneCutting: accident.laneCutting,
      policeReport: accident.policeReport,
      policeReportNumber: accident?.policeReportNumber,
      roadConditionDescription: accident.roadConditionDescription,
      accidentID: accident.id,
      state: order.state,
      capitol: capitol.name,
      subCapitol: subCapitol.name,
      vehicles: vehicles,
      classification: order.classificationIDs[0],
      pkInitKm: accident.pkInitKm,
      pkInitMeter: accident.pkInitMeter,
      pkEndKm: accident.pkEndKm,
      pkEndMeter: accident.pkEndMeter,
      concessionId: accident.concessionIDs[0],
      roadId: road.id,
      capitols: capitols,
      concessions: concessions,
      roadsValues: roads,
      subCapitolsValues: subCapitols,
      // Atención accidente data
      noticeConservationCenterDate:
        attentionAccident?.noticeConservationCenterDate,
      arrivalConservationCenterDate:
        attentionAccident?.arrivalConservationCenterDate,
      departureConservationCenterDate:
        attentionAccident?.departureConservationCenterDate,
      noticeSquadBoysDate: attentionAccident?.noticeSquadBoysDate,
      arrivalSquadBoysDate: attentionAccident?.arrivalSquadBoysDate,
      departureSquadBoysDate: attentionAccident?.departureSquadBoysDate,
      noticeRoadTeamDate: attentionAccident?.noticeRoadTeamDate,
      arrivalRoadTeamDate: attentionAccident?.arrivalRoadTeamDate,
      departureRoadTeamDate: attentionAccident?.departureRoadTeamDate,
      noticeControlCenterDate: attentionAccident?.noticeControlCenterDate,
      arrivalControlCenterDate: attentionAccident?.arrivalControlCenterDate,
      departureControlCenterDate: attentionAccident?.departureControlCenterDate,
      noticeAmbulanceCraneAssistanceDate:
        attentionAccident?.noticeAmbulanceCraneAssistanceDate,
      arrivalAmbulanceCraneAssistanceDate:
        attentionAccident?.arrivalAmbulanceCraneAssistanceDate,
      departureAmbulanceCraneAssistanceDate:
        attentionAccident?.departureAmbulanceCraneAssistanceDate,
      noticeOtherDate: attentionAccident?.noticeOtherDate,
      arrivalOtherDate: attentionAccident?.arrivalOtherDate,
      departureOtherDate: attentionAccident?.departureOtherDate,
      otherName: attentionAccident?.otherName,
      images: images,
      damages: accident.damages,
      stateOrderExpeditionParent: orderExpeditionParent.data.order.state,
      userId: order.userId,
      city: concession.location,
      predefine: order.predefinedOrderIDs?.[0],
    };
  } catch (err) {
    logSelector(
      `❌ Error en selectAccidentById para accidentId: ${accidentId}`,
      err
    );
    Sentry.captureException(err);
    console.log('[accidentes] error obteniendo los datos', err);
  }
};
